import { QueryClient } from '@tanstack/react-query'
import { fetchPage } from '@vangst/services/oogst/page'
import { RobotsEnum } from '@vangst/services/oogst/types'
import useCreateUserPasswordResetRequest from '@vangst/services/oogst/viewer/useCreateUserPasswordResetRequest'
import Routes from '@vangst/services/routes'
import { InferGetStaticPropsType } from 'next'
import { useRouter } from 'next/router'
import React from 'react'
import { useForm } from 'react-hook-form'
import Input from '../../components/forms/Input'
import Breadcrumbs from '../../components/navigation/Breadcrumbs'
import ClickyExplore from '../../components/navigation/ClickyExplore'
import PageHeader from '../../components/views/PageHeader'
import QueryErrorFallback from '../../domains/application/feedback/QueryErrorFallback'
import Meta from '../../domains/application/Meta'
import validations from '../../domains/application/validations'
import {
  GoContactSupport,
  GoSignIn,
  GoSignUpPolite,
} from '../../domains/viewer/navigation/GoDoThings'
import useAuthedRedirect from '../../domains/viewer/useAuthedRedirect'

export async function getStaticProps() {
  const queryClient = new QueryClient()
  const rawUrl = __filename.split('pages')[1]?.replace('.js', '')
  const { getPage } = await fetchPage(queryClient, { rawUrl })
  const page = { ...getPage, title: getPage?.title ?? 'Forgot Password' }
  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: null, text: 'Forgot Password' },
  ]

  return {
    props: { crumbs, page },
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function ForgotPasswordPage(props: PropsType) {
  useAuthedRedirect()

  const { crumbs, page } = props

  const defaultValues = { email: '' }
  type FormInput = typeof defaultValues

  const [scene, setScene] = React.useState(1)
  const { replace } = useRouter()
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({ defaultValues })

  const { mutate, error, isLoading } = useCreateUserPasswordResetRequest({
    onSuccess: () => setScene(2),
  })

  const submit = (fd: FormInput) => mutate({ input: { email: fd.email } })
  const send = handleSubmit(submit)
  const cancel = () => replace(`/`)

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page.metaInfo?.canonicalUrl}
        crumbs={crumbs}
        description={page.metaInfo?.description}
        image={page.pagePhotos?.[0]?.url}
        publishDate={page.postedAt}
        robots={page.metaInfo?.robots ?? RobotsEnum.IndexFollow}
        title={page.metaInfo?.title ?? page.title ?? 'Vangst | Forgot Password'}
      />
      <Breadcrumbs crumbs={crumbs} />
      <article className="mood-white">
        <PageHeader
          accent="text-white"
          className="bg-orange text-white"
          icon="people"
          title={page.title}
          subtitle={
            scene === 1
              ? "Enter your email address and we'll send an email with a link to create a new password."
              : scene === 2
              ? 'Thanks! Keep an eye out for an email with a link to reset your password.'
              : page.subtitle
          }
          description={page.description}
          imgAlt={page.pagePhotos?.[0]?.alt}
          imgSrc={page.pagePhotos?.[0]?.url}
        />
        <div className="contain-c">
          <section className="contain-xy flex max-w-prose flex-col gap-8 md:ml-24">
            {scene === 1 && (
              <form
                className="flow-y-sm"
                id="forgot-password-form"
                onSubmit={send}
                onReset={cancel}
              >
                <Input
                  error={errors?.email?.message}
                  label="email"
                  {...register('email', validations.email)}
                />
                <div className="flex w-full items-center justify-center gap-4">
                  <ClickyExplore
                    id="submit-forgot-password"
                    className="ml-auto"
                    disabled={isLoading}
                    type="submit"
                  >
                    {isLoading ? 'Sending' : 'Submit'}
                  </ClickyExplore>
                  {error != null && <QueryErrorFallback error={error} />}
                </div>
              </form>
            )}
            <div className="flow-y-xs">
              <GoSignUpPolite />
              <GoSignIn />
              <GoContactSupport />
            </div>
          </section>
        </div>
      </article>
    </>
  )
}
